import i18next from "i18next";
import { DataError } from "../../core/errors/DataError";
import { Role } from "../../core/models/entities/Role";

// Flag REACT_APP_DESIGN_CREATE_REQUIRE_ORG (23/9, OVERDRAGELSE-2026-09-23
// §7 B): the pure decisions of the "admin without a vendor" guard on the
// design create screen, kept out of the view-model, the form and the API
// client so they can be unit-tested without react-query, react-toastify or
// the router.
//
// Prod fact: CreateDesign answered 500 whenever an Admin pressed Continue
// without picking a vendor (20/8 x2, 23/9 x2). handleSubmitForm resolves
// the route org as "the picked vendor, otherwise the profile org", and an
// Admin has no profile org - so the FE built POST /organizations/null/designs
// and the API route ({orgId:required} without :int) died as a binding
// error. No design was ever created; picking a vendor first always worked.
//
// With the flag on:
//  - isDesignCreateOrgMissing blocks Continue (hint under the button + a
//    link that focuses the vendor field, toast on a forced submit);
//  - assertDesignCreateOrg makes ApiClient.createDesign refuse locally with
//    the same message before any URL is built (the safety net for every
//    other caller).
// Flag off = every helper here says "no" / passes through: byte-identical
// DOM and wire.

// i18n key of the hint / toast / error message (all 10 locale files).
export const DESIGN_CREATE_ORG_MISSING_KEY =
  "designListStyle.selectVendorBeforeContinue";
// i18n key of the link that focuses the vendor field (all 10 locale files).
export const DESIGN_CREATE_SELECT_VENDOR_KEY =
  "designListStyle.selectVendorNow";
// Marker of the local refusal (no HTTP status - no request was ever sent).
// Carried as ErrorStatus.errorCode with code 0, like the upload client
// timeout.
export const DESIGN_CREATE_ORG_MISSING_ERROR_CODE = "designCreateOrgMissing";
// DOM id the create form gives the vendor search input (flag on only) so
// the hint's link can focus it. Flag off = the input keeps MUI's own id.
export const DESIGN_CREATE_VENDOR_INPUT_ID = "design-create-vendor-search";

/**
 * The organization the create request is routed to - mirror, byte for
 * byte, of the orgId rule in useDesignCreateFormViewModel.handleSubmitForm:
 * an Admin routes to the picked vendor, everyone (and an Admin without a
 * pick) to the profile org. Null/undefined = the request would be
 * /organizations/null/designs.
 */
export function resolveDesignCreateOrgId(
  role: Role | undefined,
  selectedVendorId: number | undefined,
  profileOrganizationId: number | null | undefined
): number | null | undefined {
  return role === Role.Admin && selectedVendorId != null
    ? selectedVendorId
    : profileOrganizationId;
}

/**
 * True when Continue must be blocked: the flag is on and the org the
 * request would be routed to is null/undefined. Flag off = always false
 * (today's button, today's request), whatever the role.
 */
export function isDesignCreateOrgMissing(
  flagEnabled: boolean,
  role: Role | undefined,
  selectedVendorId: number | undefined,
  profileOrganizationId: number | null | undefined
): boolean {
  if (!flagEnabled) return false;
  return (
    resolveDesignCreateOrgId(role, selectedVendorId, profileOrganizationId) ==
    null
  );
}

/** The error the local refusal surfaces as (same text as the hint). */
export function designCreateOrgMissingError(): DataError {
  const message = i18next.t(DESIGN_CREATE_ORG_MISSING_KEY);
  return new DataError(message, {
    code: 0,
    message,
    errorCode: DESIGN_CREATE_ORG_MISSING_ERROR_CODE,
  });
}

/** True for the local refusal above (and nothing else). */
export function isDesignCreateOrgMissingError(error: unknown): boolean {
  return (
    error instanceof DataError &&
    error.errorStatus?.errorCode === DESIGN_CREATE_ORG_MISSING_ERROR_CODE
  );
}

/**
 * ApiClient guard: throws designCreateOrgMissingError when the flag is on
 * and the route org is null/undefined, so no /organizations/null/designs
 * request is ever built. Flag off = no-op, even for a missing org (today's
 * wire, byte for byte).
 */
export function assertDesignCreateOrg(
  flagEnabled: boolean,
  orgId: number | null | undefined
): void {
  if (!flagEnabled) return;
  if (orgId == null) throw designCreateOrgMissingError();
}
